// Pantalla compartida de Product Lines y Vendors (cada una tiene su ruta y su
// archivo: ProductLinesScreen.js / VendorsScreen.js). Lista por empresa con
// buscador, crear (en la madre, la hija o ambas) y editar (product line: solo la
// descripción; vendor: el formulario completo), con verificación en el mirror
// por empresa, un reintento y luego aviso a soporte de TI.
import React, { Component } from 'react';
import { AiOutlineSearch, AiOutlineReload, AiOutlinePlus, AiOutlineEdit, AiOutlineClose } from 'react-icons/ai';
import { Menu } from '../../components/Menu/Menu';
import { getValueCookie } from '../../services/cookieService';
import { COMPANIES, COMPANY_ORDER, companyName, otherCompany, listEntities } from '../../services/masterDataService';
import { C, CARD, H2, SUB, BTN_PRIMARY, BTN_SECONDARY, btn, Notice, Field, TextInput, INPUT, LABEL } from '../Inventory/NewItemWizard/ui';
import { LIMITS } from '../Inventory/NewItemWizard/wizardLogic';
import { formatPhone } from '../Maintenance/Vendors';
import NameAutocomplete from './NameAutocomplete';
import {
    KINDS, filterRows, nameMatches, exactDuplicate, counterpartSuggestions, validateName, validateCreateForm,
    emptyCreateForm, toPayloadForm, emptyTask, isBusy, runCompanyTask, vendorFormFromRow, differences, MAX_RUNS, validateVendorFields,
} from './masterDataLogic';
import '../../css/general-style.css';
import '../../css/dashboard.css';

const PAGE_SIZE = 50;
const emptyList = () => ({ loading: false, loaded: false, error: '', rows: [] });

// Campos extra del vendor (mismos que el wizard de New Item).
const VENDOR_FIELDS = [
    { id: 'address', label: 'Address line 1', col: 'col-12', max: LIMITS.address, autoComplete: 'address-line1' },
    { id: 'city', label: 'City', col: 'col-12 col-md-5', max: LIMITS.city },
    { id: 'state', label: 'State', col: 'col-5 col-md-3', max: 2, upper: true, placeholder: 'TX' },
    { id: 'zip', label: 'Zip code', col: 'col-7 col-md-4', max: 10, inputMode: 'numeric', placeholder: '12345' },
    { id: 'phone', label: 'Telephone', col: 'col-12 col-md-6', type: 'tel', inputMode: 'tel', phone: true, placeholder: '+1 (555) 123-4567' },
    { id: 'email', label: 'Email', col: 'col-12 col-md-6', type: 'email', max: LIMITS.email, inputMode: 'email', optional: true },
];

// Estado por empresa en la ventana: Not started → (en curso) → Done | Failed.
const TASK_VIEW = {
    idle: { label: 'Not started', fg: C.faint, bg: '#F1F5F9', line: C.lineStrong },
    checking: { label: 'Checking…', fg: C.info, bg: C.infoBg, line: C.infoLine, spin: true },
    sending: { label: 'Sending to MAS90…', fg: C.info, bg: C.infoBg, line: C.infoLine, spin: true },
    verifying: { label: 'Verifying…', fg: C.info, bg: C.infoBg, line: C.infoLine, spin: true },
    done: { label: 'Done', fg: C.ok, bg: C.okBg, line: C.okLine },
    failed: { label: 'Failed', fg: C.err, bg: C.errBg, line: C.errLine },
};

const sessionCompany = () => {
    const id = Number(getValueCookie('CompanyId'));
    return COMPANIES[id] ? id : 3;
};

const chip = (active, disabled) => ({
    flex: '1 1 0', minHeight: '44px', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.875rem',
    background: active ? '#FFFFFF' : 'transparent', color: active ? C.primary : C.muted,
    boxShadow: active ? '0 1px 3px rgba(15,23,42,0.15)' : 'none', cursor: disabled ? 'not-allowed' : 'pointer',
});
const TAG = { fontSize: '0.68rem', fontWeight: 700, borderRadius: '4px', padding: '0 5px', border: `1px solid ${C.line}`, color: C.faint, whiteSpace: 'nowrap' };

export default class MasterDataScreen extends Component {
    constructor(props) {
        super(props);
        this.cfg = KINDS[props.kind];
        this.state = {
            company: sessionCompany(),
            lists: { 3: emptyList(), 6: emptyList() },
            query: '',
            visible: PAGE_SIZE,
            create: null,
            rename: null,
        };
    }

    componentDidMount() {
        COMPANY_ORDER.forEach((id) => this.loadList(id));
        document.addEventListener('keydown', this.onKeyDown);
    }

    componentWillUnmount() {
        this._unmounted = true;
        document.removeEventListener('keydown', this.onKeyDown);
    }

    isCancelled = () => !!this._unmounted;
    isVendor = () => this.props.kind === 'vendor';

    onKeyDown = (e) => {
        if (e.key === 'Escape' && (this.state.create || this.state.rename) && !this.panelBusy()) this.closePanel();
    };

    // ── Listas ───────────────────────────────────────────────────────────────
    async loadList(id) {
        this.setState((s) => ({ lists: { ...s.lists, [id]: { ...s.lists[id], loading: true, error: '' } } }));
        const res = await listEntities(this.props.kind, id);
        if (this._unmounted) return;
        this.setState((s) => ({
            lists: { ...s.lists, [id]: { loading: false, loaded: res.ok, error: res.ok ? '' : res.message, rows: res.ok ? res.rows : s.lists[id].rows } },
        }));
    }

    reloadAll = () => COMPANY_ORDER.forEach((id) => this.loadList(id));

    // ── Panel común ──────────────────────────────────────────────────────────
    panelBusy() {
        const p = this.state.create || this.state.rename;
        if (!p) return false;
        return p.running || Object.keys(p.tasks).some((k) => isBusy(p.tasks[k]));
    }

    closePanel = () => {
        const p = this.state.create || this.state.rename;
        if (!p || this.panelBusy()) return;
        const touched = Object.keys(p.tasks).some((k) => p.tasks[k].sent || p.tasks[k].status === 'done');
        this.setState({ create: null, rename: null });
        if (touched) this.reloadAll();
    };

    setTask(panel, id, task) {
        this.setState((s) => {
            const p = s[panel];
            if (!p) return null;
            return { [panel]: { ...p, tasks: { ...p.tasks, [id]: task } } };
        });
    }

    // Corre una empresa (primera vez o Retry). El Retry solo existe mientras la
    // tarea no sea `final`, y se oculta en cuanto arranca (pasa a "Checking…").
    async runFor(panel, mode, id) {
        const p = this.state[panel];
        const task = p.tasks[id];
        if (!task || task.final || isBusy(task)) return;
        await runCompanyTask({
            mode, kind: this.props.kind, idCompany: id, form: toPayloadForm(p.form), task,
            onUpdate: (t) => this.setTask(panel, id, t),
            isCancelled: this.isCancelled,
        });
    }

    // Dentro de un click, React 17 aplica el setState hasta que termina el
    // handler; runAll lee las tareas de this.state, así que hay que esperar a
    // que el estado se aplique (si no, recorre 0 empresas y no manda nada).
    setStateAsync = (patch) => new Promise((resolve) => this.setState(patch, resolve));

    async runAll(panel, mode) {
        const ids = Object.keys(this.state[panel].tasks).map(Number).sort((a, b) => COMPANY_ORDER.indexOf(a) - COMPANY_ORDER.indexOf(b));
        for (let i = 0; i < ids.length; i++) {
            await this.runFor(panel, mode, ids[i]);
            if (this._unmounted) return;
        }
        this.setState((s) => (s[panel] ? { [panel]: { ...s[panel], running: false } } : null));
    }

    retry = async (panel, mode, id) => {
        if (this.panelBusy()) return;
        this.setState((s) => ({ [panel]: { ...s[panel], running: true } }));
        await this.runFor(panel, mode, id);
        if (!this._unmounted) this.setState((s) => (s[panel] ? { [panel]: { ...s[panel], running: false } } : null));
    };

    // ── Crear ────────────────────────────────────────────────────────────────
    openCreate = () => {
        this.setState({
            create: { form: emptyCreateForm(this.props.kind), errors: {}, companies: [this.state.company], started: false, running: false, tasks: {} },
        });
    };

    setCreateField(field, value) {
        this.setState((s) => ({ create: { ...s.create, form: { ...s.create.form, [field]: value }, errors: { ...s.create.errors, [field]: '' } } }));
    }

    toggleCreateCompany(id) {
        this.setState((s) => {
            const has = s.create.companies.indexOf(id) >= 0;
            const companies = has ? s.create.companies.filter((c) => c !== id) : COMPANY_ORDER.filter((c) => c === id || s.create.companies.indexOf(c) >= 0);
            return { create: { ...s.create, companies, errors: { ...s.create.errors, companies: '', name: '' } } };
        });
    }

    createErrors(create) {
        const { lists } = this.state;
        const e = validateCreateForm(this.cfg, create.form, create.companies);
        create.companies.forEach((id) => {
            if (!lists[id].loaded) e.companies = `The ${companyName(id)} list is not loaded yet — reload it to check for duplicates.`;
            const dup = !e.name && exactDuplicate(lists[id].rows, create.form.name);
            if (dup) e.name = `Already exists in ${companyName(id)} as ${dup.code} — ${dup.name}`;
        });
        return e;
    }

    submitCreate = async () => {
        const create = this.state.create;
        if (!create || create.started || this.panelBusy()) return;
        const errors = this.createErrors(create);
        if (Object.keys(errors).some((k) => errors[k])) { this.setState({ create: { ...create, errors } }); return; }
        const tasks = {};
        create.companies.forEach((id) => { tasks[id] = emptyTask(); });
        await this.setStateAsync({ create: { ...create, errors: {}, started: true, running: true, tasks } });
        await this.runAll('create', 'create');
    };

    // ── Editar (product line: descripción · vendor: formulario completo) ─────
    openRename = (row) => {
        const source = this.state.company;
        const form = this.isVendor() ? vendorFormFromRow(row) : { name: row.name };
        this.setState({
            rename: { source, row, form, alsoOther: false, counterpart: null, cpQuery: '', errors: {}, started: false, running: false, tasks: {} },
        });
    };

    setRename(patch, clearErrors) {
        this.setState((s) => {
            const errors = { ...s.rename.errors };
            (clearErrors || []).forEach((k) => { errors[k] = ''; });
            return { rename: { ...s.rename, ...patch, errors } };
        });
    }

    setRenameField(field, value) {
        this.setState((s) => ({ rename: { ...s.rename, form: { ...s.rename.form, [field]: value }, errors: { ...s.rename.errors, [field]: '', nothing: '' } } }));
    }

    renameErrors(r) {
        const { lists } = this.state;
        const kind = this.props.kind;
        let e = {};
        if (this.isVendor()) {
            e = validateVendorFields(r.form);
        } else {
            e.name = validateName(this.cfg, r.form.name);
        }
        // Debe cambiar algo, en la empresa origen o (si aplica) en la otra.
        const payload = toPayloadForm(r.form);
        const changesHere = differences(kind, r.row, payload).length > 0;
        const changesThere = r.alsoOther && r.counterpart && differences(kind, r.counterpart, payload).length > 0;
        if (!changesHere && !changesThere) e.nothing = 'Nothing changed yet';
        const dup = !e.name && exactDuplicate(lists[r.source].rows, r.form.name, r.row.code);
        if (dup) e.name = `${companyName(r.source)} already has ${dup.code} with this name`;
        if (r.alsoOther) {
            const other = otherCompany(r.source);
            if (!lists[other].loaded) e.counterpart = `The ${companyName(other)} list is not loaded — reload it.`;
            else if (!r.counterpart) e.counterpart = `Choose the matching ${this.cfg.noun} in ${companyName(other)}`;
            else {
                const dup2 = !e.name && exactDuplicate(lists[other].rows, r.form.name, r.counterpart.code);
                if (dup2) e.name = `${companyName(other)} already has ${dup2.code} with this name`;
            }
        }
        Object.keys(e).forEach((k) => { if (!e[k]) delete e[k]; });
        return e;
    }

    submitRename = async () => {
        const r = this.state.rename;
        if (!r || r.started || this.panelBusy()) return;
        const errors = this.renameErrors(r);
        if (Object.keys(errors).length) { this.setState({ rename: { ...r, errors } }); return; }
        const tasks = { [r.source]: emptyTask(r.row.code) };
        if (r.alsoOther && r.counterpart) tasks[otherCompany(r.source)] = emptyTask(r.counterpart.code);
        await this.setStateAsync({ rename: { ...r, errors: {}, started: true, running: true, tasks } });
        await this.runAll('rename', 'edit');
    };

    // ── Render: lista ────────────────────────────────────────────────────────
    renderCompanyTabs() {
        const { company, lists } = this.state;
        return (
            <div role="tablist" aria-label="Company" style={{ display: 'flex', gap: '0.25rem', background: '#F1F5F9', borderRadius: '10px', padding: '0.25rem' }}>
                {COMPANY_ORDER.map((id) => (
                    <button key={id} type="button" role="tab" aria-selected={company === id} style={chip(company === id)} onClick={() => this.setState({ company: id, visible: PAGE_SIZE })}>
                        {companyName(id)} <span style={{ fontWeight: 500, fontSize: '0.75rem' }}>· {COMPANIES[id].role.toLowerCase()}{lists[id].loaded ? ` · ${lists[id].rows.length}` : ''}</span>
                    </button>
                ))}
            </div>
        );
    }

    renderList() {
        const cfg = this.cfg;
        const isVendor = this.props.kind === 'vendor';
        const { company, lists, query, visible } = this.state;
        const list = lists[company];
        const rows = filterRows(list.rows, query).slice().sort((a, b) => a.code.localeCompare(b.code));
        const shown = rows.slice(0, visible);
        const panelOpen = !!(this.state.create || this.state.rename);

        return (
            <div style={{ ...CARD, marginTop: '1rem' }}>
                {this.renderCompanyTabs()}

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                    <div style={{ position: 'relative', flex: '1 1 240px' }}>
                        <label htmlFor="master-data-search" className="visually-hidden">Search by name or number</label>
                        <AiOutlineSearch aria-hidden="true" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: C.faint }} />
                        <input
                            id="master-data-search"
                            type="search"
                            value={query}
                            onChange={(e) => this.setState({ query: e.target.value, visible: PAGE_SIZE })}
                            placeholder={`Search by name or ${cfg.codeLabel}`}
                            style={{ ...INPUT, paddingLeft: '2.1rem' }}
                        />
                    </div>
                    <button type="button" style={btn(BTN_SECONDARY, list.loading)} disabled={list.loading} onClick={() => this.loadList(company)} aria-label="Reload list">
                        <AiOutlineReload aria-hidden="true" /> <span className="d-none d-sm-inline">Reload</span>
                    </button>
                    <button type="button" style={btn(BTN_PRIMARY, panelOpen)} disabled={panelOpen} onClick={this.openCreate}>
                        <AiOutlinePlus aria-hidden="true" /> New {cfg.noun}
                    </button>
                </div>

                {list.error && (
                    <Notice kind="error">
                        {list.error}{' '}
                        <button type="button" style={{ ...BTN_SECONDARY, minHeight: '32px', padding: '0.2rem 0.6rem' }} onClick={() => this.loadList(company)}>Try again</button>
                    </Notice>
                )}

                <div style={{ fontSize: '0.8rem', color: C.faint, margin: '0.75rem 0 0.25rem' }} aria-live="polite">
                    {list.loading ? 'Loading…' : list.loaded ? `${rows.length} of ${list.rows.length} ${cfg.noun}s in ${companyName(company)}` : ''}
                </div>

                {list.loading && !list.rows.length && (
                    <div style={{ padding: '2rem 0', textAlign: 'center', color: C.faint }}>
                        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Loading {cfg.noun}s…
                    </div>
                )}

                {!list.loading && list.loaded && !rows.length && (
                    <div style={{ padding: '1.5rem 0', textAlign: 'center', color: C.faint }}>No {cfg.noun}s match “{query}”.</div>
                )}

                {/* Teléfono: tarjetas */}
                <ul className="d-md-none" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {shown.map((r) => (
                        <li key={r.code} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', borderTop: `1px solid ${C.line}`, padding: '0.7rem 0' }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 700, color: C.faint }}>{r.code}</div>
                                <div style={{ fontWeight: 700, color: C.ink, wordBreak: 'break-word' }}>{r.name || <em style={{ color: C.faint }}>No name</em>}</div>
                                {isVendor && (r.city || r.phone) && <div style={{ fontSize: '0.8rem', color: C.muted }}>{[r.city, r.phone ? formatPhone(r.phone) : ''].filter(Boolean).join(' · ')}</div>}
                            </div>
                            <button type="button" style={btn({ ...BTN_SECONDARY, flexShrink: 0 }, panelOpen)} disabled={panelOpen} onClick={() => this.openRename(r)} aria-label={`${this.isVendor() ? 'Edit' : 'Rename'} ${r.code} ${r.name}`}>
                                <AiOutlineEdit aria-hidden="true" /> {this.isVendor() ? 'Edit' : 'Rename'}
                            </button>
                        </li>
                    ))}
                </ul>

                {/* Tablet / desktop: tabla */}
                <div className="d-none d-md-block" style={{ overflowX: 'auto' }}>
                    {shown.length > 0 && (
                        <table className="table table-hover mb-0" style={{ fontSize: '0.875rem' }}>
                            <thead>
                                <tr style={{ color: C.muted }}>
                                    <th scope="col" style={{ width: '9rem' }}>{cfg.codeLabel}</th>
                                    <th scope="col">{cfg.nameLabel}</th>
                                    {isVendor && <th scope="col">City</th>}
                                    {isVendor && <th scope="col" className="d-none d-lg-table-cell">Phone</th>}
                                    {isVendor && <th scope="col" className="d-none d-lg-table-cell">Email</th>}
                                    <th scope="col" style={{ width: '1%' }}><span className="visually-hidden">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map((r) => (
                                    <tr key={r.code}>
                                        <td style={{ fontFamily: 'monospace', fontWeight: 700, verticalAlign: 'middle' }}>{r.code}</td>
                                        <td style={{ fontWeight: 600, color: C.ink, verticalAlign: 'middle' }}>{r.name}</td>
                                        {isVendor && <td style={{ verticalAlign: 'middle' }}>{r.city}</td>}
                                        {isVendor && <td className="d-none d-lg-table-cell" style={{ verticalAlign: 'middle', whiteSpace: 'nowrap' }}>{r.phone ? formatPhone(r.phone) : ''}</td>}
                                        {isVendor && <td className="d-none d-lg-table-cell" style={{ verticalAlign: 'middle', wordBreak: 'break-all' }}>{r.email}</td>}
                                        <td style={{ verticalAlign: 'middle' }}>
                                            <button type="button" style={btn({ ...BTN_SECONDARY, minHeight: '36px', padding: '0.3rem 0.75rem' }, panelOpen)} disabled={panelOpen} onClick={() => this.openRename(r)} aria-label={`${this.isVendor() ? 'Edit' : 'Rename'} ${r.code} ${r.name}`}>
                                                <AiOutlineEdit aria-hidden="true" /> {this.isVendor() ? 'Edit' : 'Rename'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {rows.length > shown.length && (
                    <button type="button" style={{ ...BTN_SECONDARY, width: '100%', marginTop: '0.75rem' }} onClick={() => this.setState({ visible: visible + PAGE_SIZE })}>
                        Show more ({rows.length - shown.length} left)
                    </button>
                )}
            </div>
        );
    }

    // ── Render: panel (crear / renombrar) ────────────────────────────────────
    renderPanelShell(title, subtitle, body, footer) {
        const busy = this.panelBusy();
        return (
            <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '0.5rem', overflowY: 'auto' }}>
                <div role="dialog" aria-modal="true" aria-labelledby="master-data-panel-title" style={{ ...CARD, width: '100%', maxWidth: '680px', margin: 'auto 0', padding: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', padding: '1rem', borderBottom: `1px solid ${C.line}` }}>
                        <div style={{ minWidth: 0 }}>
                            <h2 id="master-data-panel-title" style={H2}>{title}</h2>
                            {subtitle && <p style={SUB}>{subtitle}</p>}
                        </div>
                        <button type="button" onClick={this.closePanel} disabled={busy} aria-label="Close" style={{ ...BTN_SECONDARY, minWidth: '44px', padding: 0, border: 'none', opacity: busy ? 0.4 : 1, cursor: busy ? 'not-allowed' : 'pointer' }}>
                            <AiOutlineClose aria-hidden="true" />
                        </button>
                    </div>
                    <div style={{ padding: '1rem' }}>{body}</div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end', padding: '0.75rem 1rem', borderTop: `1px solid ${C.line}`, background: '#F8FAFC', borderRadius: '0 0 12px 12px' }}>
                        {footer}
                    </div>
                </div>
            </div>
        );
    }

    renderCompanyCheck(id, checked, disabled, onChange, extra) {
        return (
            <label key={id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minHeight: '44px', padding: '0.5rem 0.75rem', border: `1px solid ${checked ? C.infoLine : C.line}`, background: checked ? C.infoBg : '#FFFFFF', borderRadius: '10px', cursor: disabled ? 'not-allowed' : 'pointer', flex: '1 1 200px' }}>
                <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} style={{ width: '1.1rem', height: '1.1rem' }} />
                <span>
                    <span style={{ fontWeight: 700, color: C.ink }}>{companyName(id)}</span>{' '}
                    <span style={TAG}>{COMPANIES[id].role}</span>
                    {extra}
                </span>
            </label>
        );
    }

    // Fila de estado por empresa: Not started → Checking/Sending/Verifying →
    // Done | Failed. En Failed se muestra el motivo; el Retry solo aparece la
    // primera vez y desaparece en cuanto se pulsa (la tarea pasa a Checking…).
    // Si el Retry también falla (`final`), solo queda el aviso a soporte de TI.
    renderTasks(panel, mode, detailFor) {
        const p = this.state[panel];
        const busy = this.panelBusy();
        const ids = Object.keys(p.tasks).map(Number).sort((a, b) => COMPANY_ORDER.indexOf(a) - COMPANY_ORDER.indexOf(b));
        return (
            <div style={{ marginTop: '1rem' }}>
                <div style={{ ...LABEL, marginBottom: 0 }}>Result in MAS90</div>
                {ids.map((id) => {
                    const t = p.tasks[id];
                    const v = TASK_VIEW[t.status] || TASK_VIEW.idle;
                    return (
                        <div key={id} style={{ borderTop: `1px solid ${C.line}`, padding: '0.75rem 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{ fontWeight: 700, color: C.ink, fontSize: '0.9rem' }}>{companyName(id)} · {COMPANIES[id].role}</div>
                                    <div style={{ color: C.faint, fontSize: '0.8rem', wordBreak: 'break-word' }}>{detailFor(id, t)}</div>
                                </div>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.2rem 0.55rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, color: v.fg, background: v.bg, border: `1px solid ${v.line}`, whiteSpace: 'nowrap' }}>
                                    {v.spin && <span className="spinner-border spinner-border-sm" style={{ width: '0.7rem', height: '0.7rem' }} aria-hidden="true"></span>}
                                    {v.label}
                                </span>
                            </div>
                            {t.status === 'done' && t.alreadyInMas90 && <div style={{ color: C.faint, fontSize: '0.78rem', marginTop: '0.25rem' }}>It was already saved with this data — nothing was resent.</div>}
                            {t.status === 'done' && t.zipNote && (
                                <Notice kind="info">
                                    City and State were updated by Sage to match ZIP code {t.zipNote.zip}: <strong>{t.zipNote.city || '—'}, {t.zipNote.state || '—'}</strong>. Please check that this ZIP code is correct.
                                </Notice>
                            )}
                            {t.status === 'failed' && <Notice kind={t.final ? 'warn' : 'error'} role="alert">{t.message}</Notice>}
                            {t.status === 'failed' && t.final && (
                                <div style={{ fontSize: '0.75rem', color: C.faint, marginTop: '0.35rem' }}>
                                    Reference: {companyName(id)} · {this.cfg.codeLabel} {t.code || '—'} · {new Date().toLocaleString()}
                                </div>
                            )}
                            {t.status === 'failed' && !t.final && t.runs < MAX_RUNS && (
                                <button type="button" style={btn({ ...BTN_SECONDARY, marginTop: '0.5rem' }, busy)} disabled={busy} onClick={() => this.retry(panel, mode, id)}>
                                    <AiOutlineReload aria-hidden="true" /> Retry
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    // Nombre (con sugerencias) + campos del vendor. Lo usan crear y editar.
    renderFormFields(prefix, form, shown, locked, onField, groups) {
        const cfg = this.cfg;
        return (
            <div className="row g-3" style={{ marginTop: '0.25rem' }}>
                <div className="col-12">
                    <Field id={`${prefix}-name`} label={cfg.nameLabel} required error={shown('name')} hint={`Max ${cfg.nameMax} characters · similar names are listed as you type`}>
                        <NameAutocomplete
                            id={`${prefix}-name`}
                            value={form.name}
                            onChange={(v) => onField('name', v)}
                            error={shown('name')}
                            readOnly={locked}
                            maxLength={cfg.nameMax}
                            placeholder={this.isVendor() ? 'e.g. ACME FOODS LLC' : 'e.g. FROZEN SNACKS'}
                            groups={groups}
                            useLabel={prefix.indexOf('create') >= 0 ? 'Create' : 'Use'}
                        />
                    </Field>
                </div>
                {this.isVendor() && VENDOR_FIELDS.map((f) => (
                    <div className={f.col} key={f.id}>
                        <Field id={`${prefix}-${f.id}`} label={f.label} required={!f.optional} error={shown(f.id)} hint={f.max && f.id !== 'state' ? `Max ${f.max} characters` : ''}>
                            <TextInput
                                id={`${prefix}-${f.id}`}
                                type={f.type || 'text'}
                                inputMode={f.inputMode}
                                autoComplete={f.autoComplete || 'off'}
                                placeholder={f.placeholder}
                                maxLength={f.max}
                                readOnly={locked}
                                error={shown(f.id)}
                                value={form[f.id]}
                                // Teléfono: mientras se escribe va tal cual (solo dígitos y + ( ) - espacio);
                                // el formato +1 (555) 123-4567 se aplica al salir del campo.
                                onChange={(v) => onField(f.id, f.phone ? v.replace(/[^\d+()\-\s]/g, '').slice(0, 20) : f.upper ? v.toUpperCase().replace(/[^A-Z]/g, '') : v)}
                                onBlur={f.phone ? () => { if (form.phone) onField('phone', formatPhone(form.phone)); } : undefined}
                            />
                        </Field>
                    </div>
                ))}
            </div>
        );
    }

    missingFields(form) {
        const fields = [['name', this.cfg.nameLabel]].concat(this.isVendor() ? VENDOR_FIELDS.filter((f) => !f.optional).map((f) => [f.id, f.label]) : []);
        return fields.filter(([f]) => !String(form[f] || '').trim()).map(([, label]) => label);
    }

    renderCreate() {
        const cfg = this.cfg;
        const { create, lists } = this.state;
        const { form, errors, companies, started, tasks } = create;
        const busy = this.panelBusy();
        const groups = companies.map((id) => ({
            idCompany: id,
            label: companyName(id),
            loading: lists[id].loading,
            error: lists[id].error,
            matches: nameMatches(lists[id].rows, form.name, 6),
        }));
        const ids = Object.keys(tasks);
        const allDone = started && ids.length > 0 && ids.every((id) => tasks[id].status === 'done');
        // Validación en vivo: el botón se bloquea mientras falte algo. Un campo
        // vacío no se marca en rojo (solo se lista abajo); uno con valor inválido sí.
        const live = started ? {} : this.createErrors(create);
        const shown = (f) => errors[f] || (String(form[f] || '').trim() && live[f]) || '';
        const missing = this.missingFields(form);
        const canSubmit = !started && !busy && !Object.keys(live).some((k) => live[k]);

        const body = (
            <div>
                <div style={LABEL}>Create in<span style={{ color: '#DC2626' }}> *</span></div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {COMPANY_ORDER.map((id) => this.renderCompanyCheck(id, companies.indexOf(id) >= 0, started, () => this.toggleCreateCompany(id)))}
                </div>
                {(errors.companies || live.companies) && <div style={{ color: '#DC2626', fontSize: '0.8rem', marginTop: '0.25rem' }}>{errors.companies || live.companies}</div>}
                {companies.length > 1 && !started && <div style={{ color: C.faint, fontSize: '0.78rem', marginTop: '0.25rem' }}>It is sent once per company, each with its own {cfg.codeLabel}; the parent goes first.</div>}

                {this.renderFormFields('master-data-create', form, shown, started, (f, v) => this.setCreateField(f, v), groups)}

                {started && this.renderTasks('create', 'create', (id, t) => `${t.code ? `${t.code} — ` : ''}${form.name.trim()}`)}
                {allDone && <Notice kind="ok">{cfg.Noun} created and confirmed.</Notice>}
            </div>
        );

        const footer = started ? (
            <button type="button" style={btn(BTN_PRIMARY, busy)} disabled={busy} onClick={this.closePanel}>{allDone ? 'Done' : 'Close'}</button>
        ) : (
            <React.Fragment>
                {missing.length > 0 && <span style={{ flex: '1 1 100%', fontSize: '0.78rem', color: C.faint }}>Missing: {missing.join(', ')}</span>}
                <button type="button" style={BTN_SECONDARY} onClick={this.closePanel}>Cancel</button>
                <button type="button" style={btn(BTN_PRIMARY, !canSubmit)} disabled={!canSubmit} onClick={this.submitCreate}>
                    Create in {companies.length > 1 ? 'both companies' : companies.length ? companyName(companies[0]) : 'MAS90'}
                </button>
            </React.Fragment>
        );
        return this.renderPanelShell(`New ${cfg.noun}`, busy ? 'Waiting for MAS90 — this can take a moment. Do not close this window.' : null, body, footer);
    }

    renderCounterpart(r) {
        const cfg = this.cfg;
        const { lists } = this.state;
        const other = otherCompany(r.source);
        const list = lists[other];
        const locked = r.started;
        if (locked) return null;
        const suggestions = counterpartSuggestions(list.rows, r.row, 5);
        const q = r.cpQuery.trim();
        const found = q ? filterRows(list.rows, q).slice(0, 8) : [];
        const liveError = r.errors.counterpart || this.renameErrors(r).counterpart || '';
        const option = (row, badge) => {
            const selected = r.counterpart && r.counterpart.code === row.code;
            return (
                <li key={row.code}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minHeight: '44px', padding: '0.45rem 0.75rem', borderTop: `1px solid ${C.line}`, background: selected ? C.infoBg : '#FFFFFF', cursor: 'pointer' }}>
                        <input type="radio" name="master-data-counterpart" checked={!!selected} onChange={() => this.setRename({ counterpart: row }, ['counterpart', 'name', 'nothing'])} />
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: C.faint, flexShrink: 0 }}>{row.code}</span>
                        <span style={{ flex: 1, minWidth: 0, color: C.ink, wordBreak: 'break-word' }}>{row.name}</span>
                        {badge}
                    </label>
                </li>
            );
        };
        return (
            <div style={{ marginTop: '0.75rem' }}>
                <div style={LABEL}>Matching {cfg.noun} in {companyName(other)}<span style={{ color: '#DC2626' }}> *</span></div>
                {list.loading && <div style={{ fontSize: '0.85rem', color: C.faint }}><span className="spinner-border spinner-border-sm" aria-hidden="true"></span> Loading {companyName(other)}…</div>}
                {list.error && (
                    <Notice kind="error">
                        {list.error}{' '}
                        <button type="button" style={{ ...BTN_SECONDARY, minHeight: '32px', padding: '0.2rem 0.6rem' }} onClick={() => this.loadList(other)}>Reload</button>
                    </Notice>
                )}
                {list.loaded && (
                    <div style={{ border: `1px solid ${liveError ? '#EF4444' : C.line}`, borderRadius: '10px', overflow: 'hidden' }}>
                        <div style={{ padding: '0.45rem 0.75rem', fontSize: '0.72rem', fontWeight: 700, color: C.faint, background: '#F8FAFC', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                            Suggested — same number or similar name to “{r.row.name}”
                        </div>
                        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {suggestions.map((s) => option(s.row, s.sameCode ? <span style={{ ...TAG, color: C.ok, borderColor: C.okLine }}>Same No</span> : null))}
                            {!suggestions.length && <li style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: C.faint, borderTop: `1px solid ${C.line}` }}>No suggestions — search below.</li>}
                        </ul>
                        <div style={{ padding: '0.6rem 0.75rem', borderTop: `1px solid ${C.line}`, background: '#F8FAFC' }}>
                            <label htmlFor="master-data-cp-search" style={{ ...LABEL, marginBottom: '0.25rem' }}>Not listed? Search {companyName(other)} by name or number</label>
                            <input id="master-data-cp-search" type="search" value={r.cpQuery} onChange={(e) => this.setRename({ cpQuery: e.target.value })} style={INPUT} placeholder={`Name or ${cfg.codeLabel}`} />
                        </div>
                        {q && (
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                                {found.map((row) => option(row, null))}
                                {!found.length && <li style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: C.faint, borderTop: `1px solid ${C.line}` }}>No {cfg.noun}s match “{q}”.</li>}
                            </ul>
                        )}
                    </div>
                )}
                {liveError && <div style={{ color: '#DC2626', fontSize: '0.8rem', marginTop: '0.25rem' }}>{liveError}</div>}
            </div>
        );
    }

    renderRename() {
        const cfg = this.cfg;
        const { rename: r, lists } = this.state;
        const busy = this.panelBusy();
        const other = otherCompany(r.source);
        const excludeOther = r.counterpart ? r.counterpart.code : null;
        const groups = [{ idCompany: r.source, label: companyName(r.source), loading: lists[r.source].loading, error: lists[r.source].error, matches: nameMatches(lists[r.source].rows, r.form.name, 6, r.row.code) }];
        if (r.alsoOther) groups.push({ idCompany: other, label: companyName(other), loading: lists[other].loading, error: lists[other].error, matches: nameMatches(lists[other].rows, r.form.name, 6, excludeOther) });
        const ids = Object.keys(r.tasks);
        const allDone = r.started && ids.length > 0 && ids.every((id) => r.tasks[id].status === 'done');
        const live = r.started ? {} : this.renameErrors(r);
        const shown = (f) => r.errors[f] || (String(r.form[f] || '').trim() && live[f]) || '';
        const missing = this.missingFields(r.form);
        const canSubmit = !r.started && !busy && !Object.keys(live).length;
        const verb = this.isVendor() ? 'Save' : 'Rename';
        const changed = this.isVendor()
            ? (code, current) => `${code}: ${current.name}`
            : (code, current) => `${code}: “${current.name}” → “${r.form.name.trim()}”`;

        const body = (
            <div>
                <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', background: '#F8FAFC', border: `1px solid ${C.line}`, borderRadius: '10px', padding: '0.75rem 1rem' }}>
                    {[[cfg.codeLabel, r.row.code], ['Current name', r.row.name], ['Company', companyName(r.source)]].map(([k, v]) => (
                        <div key={k} style={{ minWidth: 0 }}>
                            <div style={{ fontSize: '0.7rem', color: C.faint, fontWeight: 700, textTransform: 'uppercase' }}>{k}</div>
                            <div style={{ fontWeight: 700, color: C.ink, wordBreak: 'break-word' }}>{v}</div>
                        </div>
                    ))}
                </div>
                <p style={{ ...SUB, fontSize: '0.8rem' }}>
                    {this.isVendor()
                        ? 'The whole form is sent to MAS90 as it is: any field you change is updated too.'
                        : 'Only the description changes. Every other value stays the same in MAS90.'}
                </p>

                {this.renderFormFields('master-data-edit', r.form, shown, r.started, (f, v) => this.setRenameField(f, v), groups)}

                <div style={{ ...LABEL, marginTop: '1rem' }}>Apply to</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    {COMPANY_ORDER.map((id) => (id === r.source
                        ? this.renderCompanyCheck(id, true, true, () => {}, <span style={{ display: 'block', fontSize: '0.75rem', color: C.faint }}>{r.row.code}</span>)
                        : this.renderCompanyCheck(id, r.alsoOther, r.started, () => this.setRename({ alsoOther: !r.alsoOther, counterpart: r.alsoOther ? null : r.counterpart }, ['counterpart', 'name', 'nothing']),
                            r.alsoOther && r.counterpart ? <span style={{ display: 'block', fontSize: '0.75rem', color: C.faint }}>{r.counterpart.code} — {r.counterpart.name}</span> : null)))}
                </div>
                {r.alsoOther && this.isVendor() && !r.started && (
                    <div style={{ color: C.faint, fontSize: '0.78rem', marginTop: '0.25rem' }}>The same data is sent to {companyName(other)}, with its own {cfg.codeLabel}.</div>
                )}
                {r.alsoOther && this.renderCounterpart(r)}

                {r.started && this.renderTasks('rename', 'edit', (id, t) => changed(t.code, Number(id) === r.source ? r.row : r.counterpart))}
                {allDone && <Notice kind="ok">{this.isVendor() ? 'Vendor updated and confirmed.' : 'Description updated and confirmed.'}</Notice>}
            </div>
        );

        const n = 1 + (r.alsoOther ? 1 : 0);
        const footer = r.started ? (
            <button type="button" style={btn(BTN_PRIMARY, busy)} disabled={busy} onClick={this.closePanel}>{allDone ? 'Done' : 'Close'}</button>
        ) : (
            <React.Fragment>
                {(missing.length > 0 || live.nothing) && (
                    <span style={{ flex: '1 1 100%', fontSize: '0.78rem', color: C.faint }}>{missing.length ? `Missing: ${missing.join(', ')}` : live.nothing}</span>
                )}
                <button type="button" style={BTN_SECONDARY} onClick={this.closePanel}>Cancel</button>
                <button type="button" style={btn(BTN_PRIMARY, !canSubmit)} disabled={!canSubmit} onClick={this.submitRename}>
                    {verb} in {n > 1 ? 'both companies' : companyName(r.source)}
                </button>
            </React.Fragment>
        );
        return this.renderPanelShell(this.isVendor() ? 'Edit vendor' : `Rename ${cfg.noun}`, busy ? 'Waiting for MAS90 — this can take a moment. Do not close this window.' : null, body, footer);
    }

    render() {
        const cfg = this.cfg;
        return (
            <div className="dash-page" style={{ paddingTop: '70px', background: C.bg, minHeight: '100vh' }}>
                <Menu />
                <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #2563EB 100%)', padding: '1.5rem 1rem' }}>
                    <h1 style={{ color: 'white', fontWeight: 800, fontSize: '1.4rem', margin: 0 }}>{cfg.title}</h1>
                    <p style={{ color: 'rgba(255,255,255,0.85)', margin: '0.25rem 0 0', fontSize: '0.85rem', maxWidth: '720px' }}>
                        List, create and {this.isVendor() ? 'edit' : 'rename'} {cfg.noun}s in Mayaland (parent) and Altitude (child). Every change is confirmed after it reaches MAS90.
                    </p>
                </div>
                <div style={{ padding: '1rem', maxWidth: '1100px', margin: '0 auto' }}>
                    {this.renderList()}
                </div>
                {this.state.create && this.renderCreate()}
                {this.state.rename && this.renderRename()}
            </div>
        );
    }
}
